import axios, { AxiosError } from 'axios'
import { Profile } from '../types/persona'
import apiBase from '../utils/baseApi'

interface UploadMediaProps {
  file: Blob
  type: 'image' | 'audio' | 'video'
  name: string
}

interface UploadMediaResponse {
  success: true
  id: 'string'
  userId: 'string'
  organizationId: 'string'
  uploadUrl: 'string'
}
export interface IKnowledgeFile {
  id: string
  uploadId: string
  url: string
  createdAt: string
  fileName: string
  fileExtension: string
  fileType: string
  status: string
  errorMessage: string
  uploadUrl: string
}

interface IGetKnowledgeFiles {
  success: boolean
  data: IKnowledgeFile[]
}

interface IGetFAQs {
  items: {
    id: string
    question: string
    answer: string
    embedding: number[]
    createdAt: number
  }[]
}

interface CleanupSelfieResponse {
  success: true
  data: {
    url: string
  }
}

export const startUpload = async (
  uploadUrl: string,
  file: File | Blob,
): Promise<string> => {
  try {
    const response = await axios.put(uploadUrl, file, {
      headers: {
        'x-ms-blob-type': 'BlockBlob',
        'Content-Type': file.type,
        responseType: 'blob',
      },
    })
    if (response.status !== 201 && response.status !== 200) {
      throw new Error('Failed to upload file')
    }

    return uploadUrl
  } catch (error) {
    console.error('Error editing persona:', error)
    throw error
  }
}

export const uploadMedia = async ({ file, type, name }: UploadMediaProps) => {
  try {
    const response = await apiBase.post<UploadMediaResponse>(
      `/v2/digital-twins/uploads`,
      {
        type: type,
        originalFileName: name,
      },
    )

    if (response) {
      const uploadUrl = response.data.uploadUrl
      const url = await startUpload(uploadUrl, file)
      return url
    }

    return
  } catch (error) {
    console.error('Error editing persona:', error)
    throw error
  }
}

export const cleanupSelfie = async (image_url: string) => {
  try {
    const response = await apiBase.post<CleanupSelfieResponse>(
      `/v2/cleanup-avatars`,
      {
        image_url,
      },
    )

    return response.data
  } catch (error) {
    console.error('Error editing persona:', error)
    throw error
  }
}

export interface ImageStyle {
  id: string
  gender: string
  category: string
  title: string
  sample_url: string
}

export interface ImageStylesResponse {
  success: boolean
  data: ImageStyle[]
}

export const fetchImageStyles = async (): Promise<ImageStylesResponse> => {
  try {
    const response = await apiBase.get<ImageStylesResponse>(`/v2/image-styles`)
    return response.data
  } catch (error) {
    console.error('Error fetching LinkedIn profile:', error)
    throw error
  }
}

interface SetImageStyleArgs {
  image_url: string
  avatar_style_id: string
}

export const setImageStyle = async (
  args: SetImageStyleArgs,
): Promise<CleanupSelfieResponse> => {
  try {
    const response = await apiBase.post<CleanupSelfieResponse>(
      `/v2/style-avatars`,
      args,
    )
    return response.data
  } catch (error) {
    console.error('Error fetching LinkedIn profile:', error)
    throw error
  }
}

interface twinGenerateArgs {
  firstName: string
  lastName: string
  voiceUrl: string
  jobTitle: string
  avatarSessionId: string
  videoUrl?: string
  category?: string
  title?: string
  knowledge?: string
  company?: string
  companyId?: string
  department?: string
  gender?: string
  race?: string
  location?: string
  headline?: string
  industry?: string
  mentorPrompt?: string
  profilePictureUrl?: string
  linkedinProfile?: string
}

export interface twinGenerateResponse extends twinGenerateArgs {
  success: boolean
  id: string
  digitalTwinId: string
  generationMetadata: {
    mentorCreationSource: string
    profilePictureSource: string
  }
}

export const twinGenerate = async (
  args: twinGenerateArgs,
): Promise<twinGenerateResponse> => {
  try {
    const response = await apiBase.post<twinGenerateResponse>(
      `/v2/digital-twins`,
      { ...args, project: 'companion' },
    )

    return response.data
  } catch (error) {
    console.error('Error generating twin:', error)
    throw error
  }
}

export const deleteTwin = async (): Promise<{ success: boolean }> => {
  try {
    await apiBase.delete('/v2/digital-twins')
    return { success: true }
  } catch (error) {
    console.error('Error deleting twin:', error)
    throw error
  }
}

export interface SecurityCodeResponse {
  success: boolean
  data: string
}

export const getSecurityCode = async (): Promise<SecurityCodeResponse> => {
  try {
    const response = await apiBase.get<SecurityCodeResponse>(
      '/v2/digital-twins/security-code',
    )
    return response.data
  } catch (error) {
    console.error('Error fetching security code:', error)
    throw error
  }
}

export const validateUserConsent = async () => {
  // export const validateUserConsent = async (videoConsentUrl: string) => {
  // TODO: Mock API call - returns a promise that resolves after 5 seconds
  // Original API call is temporarily disabled
  return new Promise<{
    success: boolean
    isValid?: boolean
    message?: string
    error?: string
    details?: string
  }>(resolve => {
    setTimeout(() => {
      resolve({
        success: true,
        isValid: true,
        message: 'Consent validated successfully (mock response)',
      })
    }, 5000) // 5 seconds delay
  })

  /* Original API call code (commented out):
    try {
      const response = await apiBase.post<{
        success: boolean
        isValid?: boolean
        message?: string
        error?: string
        details?: string
      }>('/v2/validations/consent', {
        videoConsentUrl,
      })
      return response.data
    } catch (error) {
      console.error('Error validating user consent:', error)
      throw error
    }
    */
}

interface GetKnowledgeBaseResponse {
  success: boolean
  knowledge: string
  job_title: string
}

interface getKnowledgeBaseArgs {
  avatarSessionId: string
  linkedinProfile?: string
}

export const getKnowledgeBase = async ({
  avatarSessionId,
  linkedinProfile,
}: getKnowledgeBaseArgs): Promise<GetKnowledgeBaseResponse> => {
  try {
    const response = await apiBase.post<GetKnowledgeBaseResponse>(
      `/v2/knowledge-bases/${avatarSessionId}`,
      linkedinProfile ? { linkedinProfile } : {},
    )
    return response.data
  } catch (error) {
    console.error('Error fetching knowledge base:', error)
    throw error
  }
}

export interface LinkedInProfileResponse {
  success: boolean
  summary: string
  profileImageUrl: string
  profileJson: string
}

export const fetchLinkedInProfile = async (
  url: string,
): Promise<LinkedInProfileResponse> => {
  try {
    const response = await apiBase.get<LinkedInProfileResponse>(
      `/v2/linkedin/profile?url=${encodeURIComponent(url)}`,
    )
    return response.data
  } catch (error) {
    console.error('Error fetching LinkedIn profile:', error)
    throw error
  }
}

interface ApproveMentorResponse {
  success: boolean
  approveMentor: {
    success: boolean
    __typename: string
  }
  message?: string
}

export const approveMentor = async (
  mentorId: string,
): Promise<ApproveMentorResponse> => {
  try {
    const response = await apiBase.patch<ApproveMentorResponse>(
      `/v2/mentors/${mentorId}/approve`,
    )
    return response.data
  } catch (error) {
    console.error('Error approving mentor:', error)
    throw error
  }
}

// Image validation types and functions (moved from validation.ts)
export interface FaceRecognition {
  is_public_figure: boolean
  similarity: number
  name: string
}

export interface ImageValidationResponse {
  valid: boolean
  face_recognition: FaceRecognition
  message?: string
  error_message?: string
}

export const validateImage = async (
  file: File,
): Promise<ImageValidationResponse> => {
  try {
    const formData = new FormData()
    formData.append('file', file)

    const response = await apiBase.post<ImageValidationResponse>(
      '/v2/validations/image',
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      },
    )

    return response.data
  } catch (error) {
    console.error('Error validating image:', error)

    // Handle specific error cases
    if (error instanceof AxiosError && error.response?.data) {
      // Check if it's an authentication error
      if (error.response.status === 401) {
        throw new Error('Authentication failed. Please log in again.')
      }
    }

    // Return a failed validation response
    return {
      valid: false,
      face_recognition: {
        is_public_figure: false,
        similarity: 0,
        name: 'Validation failed',
      },
      message: 'Image validation failed. Please try again.',
    }
  }
}

export interface ConsentValidationRequest {
  videoConsentUrl: string
}

export interface ConsentValidationResponse {
  success: boolean
  isValid?: boolean
  message?: string
  error?: string
  details?: string
}

export const validateConsent = async (
  request: ConsentValidationRequest,
): Promise<ConsentValidationResponse> => {
  try {
    const response = await apiBase.post<ConsentValidationResponse>(
      '/v2/validations/consent',
      request,
    )

    return response.data
  } catch (error) {
    console.error('Error validating consent:', error)

    // Handle specific error cases
    if (error instanceof AxiosError && error.response?.data) {
      // Check if it's an authentication error
      if (error.response.status === 401) {
        return {
          success: false,
          isValid: false,
          error: 'Authentication failed. Please log in again.',
          details: 'Please refresh the page and try again.',
        }
      }

      // Don't expose internal error messages to users
      return {
        success: false,
        isValid: false,
        error: 'Consent validation failed. Please try again.',
        details: 'Please ensure your video is clear and try recording again.',
      }
    }

    return {
      success: false,
      isValid: false,
      error: 'Failed to validate consent',
      details: 'Please check your connection and try again.',
    }
  }
}

// Twin-specific persona fetching with custom polling behavior
export interface TwinPersonaResponse {
  data: {
    profile: {
      profile: Profile
    }
  }
}

export const fetchTwinPersonaById = async (
  id: string,
): Promise<TwinPersonaResponse> => {
  try {
    const response = await apiBase.get<TwinPersonaResponse>(`/v1/persona/${id}`)
    return response.data
  } catch (error) {
    console.error('Error fetching twin persona:', error)
    throw error
  }
}

export interface MatchImagesArgs {
  idPhotoUrl: string
  selfiePhotoUrl: string
}

export interface MatchedImagesResponse {
  confidence: number
  error: string
  is_match: boolean
  success: boolean
}

export const checkMatchedImages = async (
  args: MatchImagesArgs,
): Promise<MatchedImagesResponse> => {
  try {
    const response = await apiBase.post<MatchedImagesResponse>(
      `/v2/validations/match-images`,
      args,
    )
    return response.data
  } catch (error) {
    console.error('Error checking matched images:', error)
    throw error
  }
}

export interface Faq {
  question: string
  answer: string
}

export interface SetFaqsResponse {
  success: boolean
  data: {
    id: string
    uploadId: string
  }[]
}

export interface SetFilesResponse {
  success: boolean
}

export const setFaqsFiles = async ({
  urls,
  mentorId,
}: {
  urls: {
    url: string
    id: string
  }[]
  mentorId: string
}): Promise<SetFaqsResponse> => {
  try {
    const response = await apiBase.post<SetFaqsResponse>(
      `v2/knowledge-bases/files`,
      {
        urls,
        mentorId,
      },
    )
    return response.data
  } catch (error) {
    console.error('Error checking matched images:', error)
    throw error
  }
}

export const setFaqs = async ({
  faqs,
  mentorId,
}: {
  faqs: Faq[]
  mentorId: string
}): Promise<SetFaqsResponse> => {
  try {
    const response = await apiBase.post<SetFaqsResponse>(`/v2/faqs`, {
      faqs,
      mentorId,
    })
    return response.data
  } catch (error) {
    console.error('Error checking matched images:', error)
    throw error
  }
}

export const getKnowledgeFiles = async ({
  mentorId,
}: {
  mentorId: string
}): Promise<IGetKnowledgeFiles> => {
  try {
    const response = await apiBase.get<IGetKnowledgeFiles>(
      `/v2/mentors/${mentorId}/knowledge-bases/files`,
    )
    return response.data
  } catch (error) {
    console.error('Error fetching knowledge base:', error)
    throw error
  }
}
export const getFAQs = async ({
  mentorId,
}: {
  mentorId: string
}): Promise<IGetFAQs> => {
  try {
    const response = await apiBase.get<IGetFAQs>(`/v2/mentors/${mentorId}/faqs`)
    return response.data
  } catch (error) {
    console.error('Error fetching knowledge base:', error)
    throw error
  }
}

export const deleteFaQById = async ({
  mentorId,
  id,
}: {
  mentorId: string
  id: string
}): Promise<SetFaqsResponse> => {
  try {
    const response = await apiBase.delete<SetFaqsResponse>(
      `v2/mentors/${mentorId}/faqs/${id}`,
    )
    return response.data
  } catch (error) {
    console.error('Error deleting faq:', error)
    throw error
  }
}

export const updateFaQById = async ({
  mentorId,
  id,
}: {
  mentorId: string
  id: string
}): Promise<SetFaqsResponse> => {
  try {
    const response = await apiBase.patch<SetFaqsResponse>(
      `v2/mentors/${mentorId}/faqs/${id}`,
    )
    return response.data
  } catch (error) {
    console.error('Error deleting faq:', error)
    throw error
  }
}

export const updateFaQs = async ({
  mentorId,
  faqs,
}: {
  mentorId: string
  faqs: Faq[]
}): Promise<SetFaqsResponse> => {
  try {
    const response = await apiBase.patch<SetFaqsResponse>(
      `v2/mentors/${mentorId}/faqs`,
      {
        faqs,
      },
    )
    return response.data
  } catch (error) {
    console.error('Error deleting faq:', error)
    throw error
  }
}
